// =============================================================================
// Reactstrap shim — drop-in replacement that renders plain HTML using the
// theme classes in src/styles/theme.css. No bootstrap, no reactstrap runtime.
//
// The component API mirrors reactstrap just closely enough to satisfy every
// existing call site without code changes. Anything more exotic (e.g. Popover,
// Tooltip) is intentionally omitted — none of the app screens use it.
// =============================================================================

import React, {
  Children,
  cloneElement,
  createContext,
  forwardRef,
  isValidElement,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

// ---------- Helpers ----------------------------------------------------------

const cx = (...parts) => parts.filter(Boolean).join(' ');

const COLOR_ALIASES = {
  muted: 'secondary',
};

const resolveColor = (color) => (color ? COLOR_ALIASES[color] || color : '');

const passDomProps = (props) => {
  // Filter out reactstrap-only props so they don't leak onto DOM nodes.
  const out = {};
  const skip = new Set([
    'color', 'outline', 'size', 'block', 'pill', 'fade',
    'tag', 'toggle', 'isOpen', 'caret', 'inNavbar', 'nav',
    'vertical', 'centered', 'check', 'checkInline', 'inline',
    'disabled', 'active',
    // Routing / navigation props handled explicitly by the components that
    // use them; spreading them from `...rest` would clobber the explicit
    // versions above and break React-Router <Link>.
    'to', 'href', 'replace', 'state',
    // Reactstrap-style inner ref — handled in components that need it.
    'innerRef',
  ]);
  for (const k of Object.keys(props)) {
    if (skip.has(k)) continue;
    out[k] = props[k];
  }
  return out;
};

// ---------- Layout primitives ------------------------------------------------

export const Container = ({ fluid, className, children, ...rest }) => {
  const cls = cx(fluid ? 'container-fluid' : 'container', className);
  return <div className={cls} {...passDomProps(rest)}>{children}</div>;
};

export const Row = ({ className, children, noGutters, ...rest }) => {
  const cls = cx('row', noGutters ? 'g-0' : null, className);
  return <div className={cls} {...passDomProps(rest)}>{children}</div>;
};

const Col = ({ xs, sm, md, lg, xl, xxl, className, children, ...rest }) => {
  const sizeClasses = [];
  if (xs != null) sizeClasses.push(xs === true ? 'col' : `col-${xs}`);
  if (sm != null) sizeClasses.push(sm === true ? 'col-sm' : `col-sm-${sm}`);
  if (md != null) sizeClasses.push(md === true ? 'col-md' : `col-md-${md}`);
  if (lg != null) sizeClasses.push(lg === true ? 'col-lg' : `col-lg-${lg}`);
  if (xl != null) sizeClasses.push(xl === true ? 'col-xl' : `col-xl-${xl}`);
  if (xxl != null) sizeClasses.push(xxl === true ? 'col-xxl' : `col-xxl-${xxl}`);
  const cls = cx(...sizeClasses, className);
  return <div className={cls} {...passDomProps(rest)}>{children}</div>;
};
export { Col };

// ---------- Cards ------------------------------------------------------------

export const Card = ({ className, children, ...rest }) => (
  <div className={cx('card', className)} {...passDomProps(rest)}>{children}</div>
);

export const CardBody = ({ className, children, ...rest }) => (
  <div className={cx('card-body', className)} {...passDomProps(rest)}>{children}</div>
);

export const CardHeader = ({ className, children, ...rest }) => (
  <div className={cx('card-header', className)} {...passDomProps(rest)}>{children}</div>
);

export const CardFooter = ({ className, children, ...rest }) => (
  <div className={cx('card-footer', className)} {...passDomProps(rest)}>{children}</div>
);

export const CardTitle = ({ className, children, tag: Tag = 'h5', ...rest }) => (
  <Tag className={cx('card-title', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const CardSubtitle = ({ className, children, tag: Tag = 'h6', ...rest }) => (
  <Tag className={cx('card-subtitle', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const CardText = ({ className, children, tag: Tag = 'p', ...rest }) => (
  <Tag className={cx('card-text', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const CardLink = ({ className, children, ...rest }) => (
  <a className={cx('card-link', className)} {...passDomProps(rest)}>{children}</a>
);

// ---------- Buttons ----------------------------------------------------------

const Button = forwardRef(function Button(
  { color, outline, size, block, pill, className, children, tag: Tag, to, href, ...rest },
  ref,
) {
  const TagToUse = Tag || 'button';
  const classes = cx(
    'btn',
    color && (outline ? `btn-outline-${resolveColor(color)}` : `btn-${resolveColor(color)}`),
    size === 'sm' && 'btn-sm',
    size === 'lg' && 'btn-lg',
    block && 'btn-block',
    pill && 'badge-pill',
    className,
  );
  // For non-button tags (e.g. Link) avoid passing type/onClick conflict.
  const extra = {};
  if (TagToUse === 'button' && !('type' in rest)) extra.type = 'button';
  // Routing props must be forwarded to the inner tag — passDomProps strips
  // them from `...rest`, so add them back explicitly here so React-Router's
  // <Link> receives the `to` it needs to navigate.
  return (
    <TagToUse ref={ref} className={classes} to={to} href={href} {...extra} {...passDomProps(rest)}>
      {children}
    </TagToUse>
  );
});
export { Button };

export const ButtonGroup = ({ className, children, ...rest }) => (
  <div className={cx('btn-group', className)} role="group" {...passDomProps(rest)}>
    {children}
  </div>
);

export const ButtonToolbar = ({ className, children, ...rest }) => (
  <div className={cx('btn-toolbar', className)} role="toolbar" {...passDomProps(rest)}>
    {children}
  </div>
);

// ---------- Forms ------------------------------------------------------------

export const Form = ({ className, children, inline, ...rest }) => (
  <form className={cx(inline ? 'form-inline' : null, className)} {...passDomProps(rest)}>
    {children}
  </form>
);

export const FormGroup = ({ className, children, check, checkInline, inline, ...rest }) => (
  <div
    className={cx(
      check || checkInline ? 'form-check' : 'form-group',
      (check || checkInline) && inline ? 'form-check-inline' : null,
      className,
    )}
    {...passDomProps(rest)}
  >
    {children}
  </div>
);

export const FormText = ({ className, children, color, ...rest }) => (
  <small
    className={cx('form-text', color && `text-${resolveColor(color)}`, className)}
    {...passDomProps(rest)}
  >
    {children}
  </small>
);

export const Label = ({ className, children, check, for: htmlFor, ...rest }) => (
  <label
    htmlFor={htmlFor}
    className={cx(check ? 'form-check-label' : 'form-label', className)}
    {...passDomProps(rest)}
  >
    {children}
  </label>
);

const Input = forwardRef(function Input(
  { type = 'text', className, plaintext, bsSize, children, innerRef, ...rest },
  ref,
) {
  const isCheckboxOrRadio = type === 'checkbox' || type === 'radio';
  const isRange = type === 'range';
  const isFile = type === 'file';
  const isPlain = plaintext;
  // `<Input type="select">` (a Reactstrap-ism) renders a `<select>` element
  // that needs <option> children — it can't be an `<input>` because that's
  // a void element and would throw at runtime.
  const isSelect = type === 'select' || type === 'select-multiple';

  // Reactstrap's `<Input>` accepts `innerRef` (its own convention) to attach
  // a ref to the underlying DOM element. Our shim also accepts plain `ref`
  // via forwardRef. Prefer the explicit ref / innerRef if provided, fall
  // back to forwardRef's `ref` arg otherwise.
  const effectiveRef = innerRef || ref;

  if (isSelect) {
    const classes = cx(
      'form-select',
      bsSize === 'sm' && 'form-select-sm',
      bsSize === 'lg' && 'form-select-lg',
      className,
    );
    return (
      <select
        ref={effectiveRef}
        className={classes}
        multiple={type === 'select-multiple'}
        {...passDomProps(rest)}
      >
        {children}
      </select>
    );
  }

  const classes = cx(
    // `form-control` is a stretched, block-level input — wrong for
    // checkbox/radio (which need `form-check-input`) and for plain text
    // (which uses `form-control-plaintext`). Only attach it for the
    // actually-stretched types: text, password, email, etc. range, file
    // and checkbox/radio get their own Bootstrap class instead.
    !isPlain && !isCheckboxOrRadio && !isRange ? 'form-control' : null,
    isPlain && 'form-control-plaintext',
    bsSize === 'sm' && 'form-control-sm',
    bsSize === 'lg' && 'form-control-lg',
    isCheckboxOrRadio && 'form-check-input',
    isRange && 'form-range',
    isFile && 'form-control',
    className,
  );

  return <input ref={effectiveRef} type={type} className={classes} {...passDomProps(rest)} />;
});
export { Input };

export const Textarea = forwardRef(function Textarea({ className, innerRef, ...rest }, ref) {
  return <textarea ref={innerRef || ref} className={cx('form-control', className)} {...passDomProps(rest)} />;
});

export const Select = forwardRef(function Select({ className, children, innerRef, ...rest }, ref) {
  return <select ref={innerRef || ref} className={cx('form-select', className)} {...passDomProps(rest)}>{children}</select>;
});

// ---------- Alerts -----------------------------------------------------------

export const Alert = ({
  color = 'primary',
  className,
  children,
  toggle,
  isOpen = true,
  fade = true,
  ...rest
}) => {
  if (!isOpen) return null;
  const cls = cx(
    'alert',
    `alert-${resolveColor(color)}`,
    fade && 'fade',
    toggle && 'alert-dismissible',
    className,
  );
  return (
    <div role="alert" className={cls} {...passDomProps(rest)}>
      {children}
      {toggle && (
        <button
          type="button"
          className="alert-close"
          aria-label="Close"
          onClick={toggle}
        >
          ×
        </button>
      )}
    </div>
  );
};

// ---------- Spinner ----------------------------------------------------------

export const Spinner = ({ color, size, className, children, type, ...rest }) => {
  const cls = cx(
    'spinner-border',
    size === 'sm' && 'spinner-border-sm',
    color && `text-${resolveColor(color)}`,
    className,
  );
  return (
    <span role="status" className={cls} {...passDomProps(rest)}>
      <span className="visually-hidden">Loading...</span>
      {children}
    </span>
  );
};

// ---------- Tables -----------------------------------------------------------

export const Table = ({ className, children, size, bordered, borderless, hover, striped, dark, responsive, ...rest }) => {
  const cls = cx(
    'table',
    size === 'sm' && 'table-sm',
    bordered && 'table-bordered',
    borderless && 'table-borderless',
    hover && 'table-hover',
    striped && 'table-striped',
    dark && 'table-dark',
    className,
  );
  if (responsive) {
    return (
      <div className="table-responsive">
        <table className={cls} {...passDomProps(rest)}>{children}</table>
      </div>
    );
  }
  return <table className={cls} {...passDomProps(rest)}>{children}</table>;
};

// ---------- Pagination -------------------------------------------------------

export const Pagination = ({ className, children, size, listClassName, ...rest }) => {
  const cls = cx('pagination', size && `pagination-${size}`, className);
  return (
    <ul className={cls} {...passDomProps(rest)}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        return cloneElement(child, { _list: true });
      })}
    </ul>
  );
};

export const PaginationItem = ({ className, children, active, disabled, ...rest }) => {
  const cls = cx('page-item', active && 'active', disabled && 'disabled', className);
  return (
    <li className={cls} {...passDomProps(rest)}>
      {Children.map(children, (child) => {
        if (!isValidElement(child)) return child;
        if (disabled && child.props && child.props.onClick) {
          return cloneElement(child, { onClick: undefined, tabIndex: -1, 'aria-disabled': true });
        }
        return child;
      })}
    </li>
  );
};

const PaginationLink = forwardRef(function PaginationLink(
  { className, children, previous, next, first, last, active, ...rest },
  ref,
) {
  let text = children;
  if (first) text = '«';
  else if (previous) text = '‹';
  else if (next) text = '›';
  else if (last) text = '»';
  const cls = cx('page-link', className);
  return <a ref={ref} className={cls} {...passDomProps(rest)}>{text}</a>;
});
export { PaginationLink };

// ---------- Nav / Navbar -----------------------------------------------------

const NavContext = createContext(null);

export const Nav = ({ className, children, vertical, horizontal, tabs, pills, fill, justified, ...rest }) => {
  const cls = cx(
    'nav',
    vertical && 'flex-column',
    tabs && 'nav-tabs',
    pills && 'nav-pills',
    fill && 'nav-fill',
    justified && 'nav-justified',
    className,
  );
  return (
    <NavContext.Provider value={{ vertical: !!vertical, pills: !!pills, tabs: !!tabs }}>
      <ul className={cls} {...passDomProps(rest)}>{children}</ul>
    </NavContext.Provider>
  );
};

export const NavItem = ({ className, children, active, ...rest }) => {
  const cls = cx('nav-item', className);
  return <li className={cls} {...passDomProps(rest)}>{children}</li>;
};

export const NavLink = ({ className, children, tag: Tag, active, disabled, href, to, ...rest }) => {
  const ctx = useContext(NavContext);
  const TagToUse = Tag || (ctx?.pills ? 'a' : 'a');
  const cls = cx(
    'nav-link',
    ctx?.tabs && 'nav-link',
    active && 'active',
    disabled && 'disabled',
    className,
  );
  if (TagToUse !== 'a') {
    return <TagToUse className={cls} to={to} {...passDomProps(rest)}>{children}</TagToUse>;
  }
  const linkProps = { ...passDomProps(rest), href };
  if (to) linkProps.href = to;
  return <TagToUse className={cls} {...linkProps}>{children}</TagToUse>;
};

export const Navbar = ({ className, children, light, dark, expand, container, ...rest }) => {
  const cls = cx('navbar', expand && `navbar-expand-${expand}`, light && 'navbar-light', dark && 'navbar-dark', className);
  if (container === 'fluid') {
    return (
      <nav className={cls} {...passDomProps(rest)}>
        <div className="container-fluid">{children}</div>
      </nav>
    );
  }
  if (container) {
    return (
      <nav className={cls} {...passDomProps(rest)}>
        <div className="container">{children}</div>
      </nav>
    );
  }
  return <nav className={cls} {...passDomProps(rest)}>{children}</nav>;
};

export const NavbarBrand = ({ className, children, tag: Tag = 'a', href, to, ...rest }) => {
  const TagToUse = Tag;
  if (TagToUse === 'a') {
    return (
      <TagToUse
        className={cx('navbar-brand', className)}
        href={href || to}
        {...passDomProps(rest)}
      >
        {children}
      </TagToUse>
    );
  }
  // For component tags like React-Router's <Link>, forward both `to` and `href`
  // so whichever the consumer expects gets a value.
  return (
    <TagToUse
      className={cx('navbar-brand', className)}
      to={to}
      href={href}
      {...passDomProps(rest)}
    >
      {children}
    </TagToUse>
  );
};

export const NavbarToggler = ({ className, children, onClick, tag: Tag = 'button', ...rest }) => {
  const tagProps = Tag === 'button'
    ? { type: 'button', ...passDomProps(rest) }
    : passDomProps(rest);
  return (
    <Tag className={cx('navbar-toggler', className)} onClick={onClick} {...tagProps}>
      {children || (
        <span className="navbar-toggler-icon" aria-hidden="true" />
      )}
    </Tag>
  );
};

export const NavbarText = ({ className, children, ...rest }) => (
  <span className={cx('navbar-text', className)} {...passDomProps(rest)}>{children}</span>
);

// ---------- Collapse ---------------------------------------------------------

export const Collapse = ({ isOpen, children, className, navbar, ...rest }) => {
  const cls = cx('collapse', navbar && 'navbar-collapse', isOpen && 'show', className);
  // No inline `display` here — the responsive rules in theme.css (the
  // `navbar-expand-*` media queries) decide when the collapse is shown on
  // wider screens. Adding an inline `display: none` would break that.
  return (
    <div className={cls} {...passDomProps(rest)}>{children}</div>
  );
};

// ---------- Dropdown ---------------------------------------------------------

const DropdownContext = createContext(null);

export const Dropdown = ({ isOpen, toggle, direction, children, className, ...rest }) => {
  // Start closed when neither `isOpen` nor `defaultOpen` was provided.
  const [internalOpen, setInternalOpen] = useState(!!isOpen);
  const controlled = isOpen !== undefined;
  const open = controlled ? !!isOpen : internalOpen;

  // Stable callbacks (don't change identity on each render).
  const close = useRef();
  close.current = () => {
    if (controlled) toggle && toggle();
    else setInternalOpen(false);
  };
  const openIt = useRef();
  openIt.current = () => {
    if (controlled) toggle && toggle();
    else setInternalOpen(true);
  };

  const onToggle = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (open) close.current();
    else openIt.current();
  };

  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        close.current();
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const cls = cx('dropdown', direction && `drop${direction}`, open && 'show', className);
  return (
    <DropdownContext.Provider value={{ open, onToggle, direction }}>
      <div ref={containerRef} className={cls} {...passDomProps(rest)}>{children}</div>
    </DropdownContext.Provider>
  );
};

export const UncontrolledDropdown = ({ defaultOpen, children, direction, className, ...rest }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const containerRef = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const onToggle = (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setOpen((v) => !v);
  };

  const cls = cx('dropdown', direction && `drop${direction}`, open && 'show', className);
  return (
    <DropdownContext.Provider value={{ open, onToggle, direction }}>
      <div ref={containerRef} className={cls} {...passDomProps(rest)}>{children}</div>
    </DropdownContext.Provider>
  );
};

export const DropdownToggle = ({ className, children, tag: Tag = 'button', caret, nav, split, href, onClick, ...rest }) => {
  const ctx = useContext(DropdownContext);
  const cls = cx(
    'dropdown-toggle',
    nav && 'nav-link',
    split && 'dropdown-toggle-split',
    className,
  );

  const tagProps = Tag === 'button'
    ? { type: 'button', 'aria-expanded': ctx?.open ? 'true' : 'false', ...passDomProps(rest) }
    : passDomProps(rest);
  if (Tag === 'a' && href) tagProps.href = href;

  const handleClick = (e) => {
    onClick && onClick(e);
    if (!e.defaultPrevented) ctx && ctx.onToggle && ctx.onToggle(e);
  };

  return (
    <Tag className={cls} onClick={handleClick} {...tagProps}>
      {children}
      {caret !== false && <span className="dropdown-caret" aria-hidden="true" />}
    </Tag>
  );
};

export const DropdownMenu = ({ className, children, end, start, ...rest }) => {
  const ctx = useContext(DropdownContext);
  const cls = cx(
    'dropdown-menu',
    ctx?.open && 'show',
    end && 'dropdown-menu-end',
    start && 'dropdown-menu-start',
    className,
  );
  return (
    <div className={cls} {...passDomProps(rest)} role="menu">
      {children}
    </div>
  );
};

export const DropdownItem = ({
  className,
  children,
  onClick,
  tag: Tag = 'button',
  divider,
  header,
  disabled,
  active,
  href,
  to,
  toggle = true,
  ...rest
}) => {
  // Hooks must run unconditionally — call useContext before any early-return.
  const ctx = useContext(DropdownContext);

  if (divider) return <div className="dropdown-divider" role="separator" />;
  if (header) return <h6 className="dropdown-header">{children}</h6>;
  const cls = cx('dropdown-item', active && 'active', disabled && 'disabled', className);

  const handleClick = (e) => {
    if (disabled) return;
    // Run the consumer's click handler first so anything it does (e.g.
    // react-router-dom <Link> calling preventDefault and dispatching a
    // navigation) happens before we tear the menu down.
    onClick && onClick(e);
    // Bootstrap auto-closes the menu on item click unless the consumer
    // explicitly disabled it via `toggle={false}`. Defer to the next tick
    // so the click handler's navigation (or any state update) completes
    // first; closing the dropdown before navigation can prevent the SPA
    // route change from firing.
    if (toggle !== false && ctx && ctx.onToggle) {
      setTimeout(() => ctx.onToggle(e), 0);
    }
  };

  if (Tag !== 'button') {
    const linkProps = { ...passDomProps(rest) };
    if (Tag === 'a' && (href || to)) linkProps.href = href || to;
    return (
      <Tag className={cls} onClick={handleClick} to={to} {...linkProps}>
        {children}
      </Tag>
    );
  }

  return (
    <button
      type="button"
      className={cls}
      onClick={handleClick}
      disabled={disabled}
      {...passDomProps(rest)}
    >
      {children}
    </button>
  );
};

export const DropdownHeader = ({ className, children, ...rest }) => (
  <h6 className={cx('dropdown-header', className)} {...passDomProps(rest)}>{children}</h6>
);

// ---------- Modal ------------------------------------------------------------

export const Modal = ({
  isOpen,
  toggle,
  onClosed,
  children,
  size,
  centered,
  className,
  backdrop = true,
  keyboard = true,
  fade,
  zIndex,
  ...rest
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e) => {
      if (keyboard && e.key === 'Escape') {
        toggle && toggle(e);
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen, keyboard, toggle]);

  if (!isOpen) return null;
  const overlayCls = cx('modal-overlay', centered && 'modal-centered');
  const modalCls = cx(
    'modal',
    size === 'sm' && 'modal-sm',
    size === 'lg' && 'modal-lg',
    size === 'xl' && 'modal-xl',
    className,
  );
  return (
    <div
      className={overlayCls}
      role="dialog"
      onClick={(e) => {
        if (backdrop && e.target === e.currentTarget) toggle && toggle(e);
      }}
      style={zIndex ? { zIndex } : undefined}
    >
      <div className={modalCls} {...passDomProps(rest)}>
        {children}
      </div>
    </div>
  );
};

export const ModalHeader = ({ className, children, toggle, close, ...rest }) => (
  <div className={cx('modal-header', className)} {...passDomProps(rest)}>
    <h5 className="modal-title">{children}</h5>
    {(toggle || close) && (
      <button
        type="button"
        className="modal-close"
        aria-label="Close"
        onClick={toggle || close}
      >
        ×
      </button>
    )}
  </div>
);

export const ModalBody = ({ className, children, ...rest }) => (
  <div className={cx('modal-body', className)} {...passDomProps(rest)}>{children}</div>
);

export const ModalFooter = ({ className, children, ...rest }) => (
  <div className={cx('modal-footer', className)} {...passDomProps(rest)}>{children}</div>
);

// ---------- Toast (no-op, app uses its own toast container) ------------------

export const Toast = ({ children, className, isOpen, ...rest }) => {
  if (isOpen === false) return null;
  return <div className={cx('toast', className)} {...passDomProps(rest)}>{children}</div>;
};

export const ToastHeader = ({ className, children, toggle, close, ...rest }) => (
  <div className={cx('toast-header', className)} {...passDomProps(rest)}>
    {children}
    {(toggle || close) && (
      <button type="button" className="toast-close" aria-label="Close" onClick={toggle || close}>×</button>
    )}
  </div>
);

export const ToastBody = ({ className, children, ...rest }) => (
  <div className={cx('toast-body', className)} {...passDomProps(rest)}>{children}</div>
);

// ---------- Badge ------------------------------------------------------------

export const Badge = ({
  color = 'primary',
  className,
  pill,
  children,
  tag: Tag = 'span',
  ...rest
}) => {
  const cls = cx(
    'badge',
    `badge-${resolveColor(color)}`,
    pill && 'badge-pill',
    className,
  );
  return <Tag className={cls} {...passDomProps(rest)}>{children}</Tag>;
};

// ---------- Misc helpers -----------------------------------------------------

export const Progress = ({ value = 0, max = 100, color, striped, animated, className, children, ...rest }) => {
  const cls = cx('progress', className);
  return (
    <div className={cls} {...passDomProps(rest)}>
      <div
        className={cx(
          'progress-bar',
          color && `bg-${resolveColor(color)}`,
          striped && 'progress-bar-striped',
          animated && 'progress-bar-animated',
        )}
        role="progressbar"
        style={{ width: `${Math.min(100, (value / max) * 100)}%` }}
        aria-valuenow={value}
        aria-valuemin={0}
        aria-valuemax={max}
      >
        {children}
      </div>
    </div>
  );
};

export const ListGroup = ({ className, children, flush, ...rest }) => (
  <ul className={cx('list-group', flush && 'list-group-flush', className)} {...passDomProps(rest)}>
    {children}
  </ul>
);

export const ListGroupItem = ({ className, children, active, disabled, action, tag: Tag = 'li', ...rest }) => (
  <Tag
    className={cx(
      'list-group-item',
      active && 'active',
      disabled && 'disabled',
      action && 'list-group-item-action',
      className,
    )}
    {...passDomProps(rest)}
  >
    {children}
  </Tag>
);

export const ListGroupItemHeading = ({ className, children, tag: Tag = 'h5', ...rest }) => (
  <Tag className={cx('list-group-item-heading', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const ListGroupItemText = ({ className, children, tag: Tag = 'p', ...rest }) => (
  <Tag className={cx('list-group-item-text', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const Jumbotron = ({ className, children, fluid, tag: Tag = 'div', ...rest }) => (
  <Tag className={cx('jumbotron', fluid && 'jumbotron-fluid', className)} {...passDomProps(rest)}>{children}</Tag>
);

export const Breadcrumb = ({ className, children, ...rest }) => (
  <nav aria-label="breadcrumb">
    <ol className={cx('breadcrumb', className)} {...passDomProps(rest)}>{children}</ol>
  </nav>
);

export const BreadcrumbItem = ({ className, children, active, tag: Tag = 'li', ...rest }) => (
  <Tag
    className={cx('breadcrumb-item', active && 'active', className)}
    aria-current={active ? 'page' : undefined}
    {...passDomProps(rest)}
  >
    {children}
  </Tag>
);

// ---------- Default export (so `import R from 'reactstrap'` keeps working) --

const Reactstrap = {
  Container, Row, Col,
  Card, CardBody, CardHeader, CardFooter, CardTitle, CardSubtitle, CardText, CardLink,
  Button, ButtonGroup, ButtonToolbar,
  Form, FormGroup, FormText, Label, Input, Textarea, Select,
  Alert, Spinner,
  Table, Pagination, PaginationItem, PaginationLink,
  Nav, NavItem, NavLink, Navbar, NavbarBrand, NavbarToggler, NavbarText,
  Collapse,
  Dropdown, UncontrolledDropdown, DropdownToggle, DropdownMenu, DropdownItem, DropdownHeader,
  Modal, ModalHeader, ModalBody, ModalFooter,
  Toast, ToastHeader, ToastBody,
  Badge, Progress,
  ListGroup, ListGroupItem, ListGroupItemHeading, ListGroupItemText,
  Jumbotron, Breadcrumb, BreadcrumbItem,
};

export default Reactstrap;